import { useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { clientsApi, listClients } from '../../features/clientes/api';
import type { ClientListItem } from '../../features/clientes/types';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { formatDocumento } from '../utils/document';

export interface ClienteSelectProps {
  value: number | null;
  onChange: (id: number | null, item: ClientListItem | null) => void;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  /** Inclui também clientes inativos no resultado. Default false. */
  incluirInativos?: boolean;
  /**
   * Restringe a lista a clientes onde o colaborador `<id>` está alocado em
   * algum projeto (T-20b). Quando setado, propaga `apontavelParaColaborador`
   * para o upstream.
   */
  apontavelParaColaborador?: number | null;
}

const PAGE_SIZE = 50;

export function ClienteSelect({
  value,
  onChange,
  label,
  placeholder,
  error,
  helperText,
  disabled,
  incluirInativos,
  apontavelParaColaborador,
}: ClienteSelectProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const search = useDebouncedValue(input, 250);

  const list = useQuery({
    queryKey: ['clients', 'select', { search, incluirInativos, apontavelParaColaborador }],
    queryFn: () =>
      listClients(
        {
          page: 1,
          pageSize: PAGE_SIZE,
          sortBy: 'nome',
          sortOrder: 'asc',
          search: search || undefined,
          includeInactive: incluirInativos ?? false,
        },
        { apontavelParaColaborador: apontavelParaColaborador ?? null },
      ),
    enabled: open,
    placeholderData: (previous) => previous,
  });

  const detail = useQuery({
    queryKey: ['clients', 'detail', value],
    queryFn: () => clientsApi.get(value as number),
    enabled: value !== null && value !== undefined,
  });

  const options: ClientListItem[] = list.data?.data ?? [];
  const selectedFromList = options.find((o) => o.id === value) ?? null;
  const selected: ClientListItem | null =
    selectedFromList ??
    (detail.data
      ? {
          id: detail.data.id,
          nome: detail.data.nome,
          tipoPessoa: detail.data.tipoPessoa,
          documento: detail.data.documento,
          telefone: detail.data.telefone,
          contato: detail.data.contato,
          ativo: detail.data.ativo,
          criadoEm: detail.data.criadoEm,
          criadoPorNome: detail.data.criadoPorNome,
          atualizadoEm: detail.data.atualizadoEm,
        }
      : null);

  return (
    <Autocomplete<ClientListItem>
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={selected}
      onChange={(_, v) => onChange(v?.id ?? null, v)}
      onInputChange={(_, v, reason) => {
        if (reason === 'input') setInput(v);
      }}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionLabel={(o) => `${o.nome} — ${formatDocumento(o.documento, o.tipoPessoa)}`}
      options={options}
      loading={list.isFetching}
      disabled={disabled}
      noOptionsText={
        list.isError ? t('components.asyncSelect.error') : t('components.asyncSelect.noOptions')
      }
      loadingText={t('components.asyncSelect.loading')}
      filterOptions={(x) => x}
      renderOption={(props, option) => {
        // key vem em `props` no MUI novo — extrair pra não cair no spread.
        const { key, ...liProps } = props as typeof props & { key?: string };
        return (
          <Box component="li" key={key ?? option.id} {...liProps}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {option.nome}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                {formatDocumento(option.documento, option.tipoPessoa)}
              </Typography>
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? t('components.clienteSelect.label')}
          placeholder={placeholder ?? t('components.clienteSelect.placeholder')}
          error={error || list.isError}
          helperText={list.isError ? t('components.asyncSelect.error') : helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {list.isFetching ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
